import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts"
import { Box, Typography, useTheme } from "@mui/material"
import type { ChatAnalyze } from "../interfaces"

const WEEKDAY_NAMES = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"]

interface WeekdayBarChartProps {
  stats: ChatAnalyze.Stats
}

export function WeekdayBarChart({ stats }: WeekdayBarChartProps) {
  const theme = useTheme()

  const data = WEEKDAY_NAMES.map((name, i) => ({
    name,
    count: stats.weekdayMsgs[String(i)] ?? 0,
  }))

  const maxCount = Math.max(...data.map((d) => d.count), 1)
  const bestIdx = data.findIndex((d) => d.count === maxCount)

  return (
    <Box>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart
          layout="vertical"
          data={data}
          margin={{ top: 4, right: 8, left: 4, bottom: 4 }}
        >
          <XAxis
            type="number"
            tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
            tickFormatter={(v: number) => v.toLocaleString("ru-RU")}
          />
          <YAxis
            type="category"
            dataKey="name"
            width={28}
            tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
          />
          <Tooltip
            labelFormatter={(label) => `День: ${label}`}
            formatter={(value: number | undefined) => [
              (value ?? 0).toLocaleString("ru-RU"),
              "Сообщений",
            ]}
            labelStyle={{ color: theme.palette.text.secondary }}
            itemStyle={{ color: theme.palette.text.primary }}
            contentStyle={{
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 4,
              fontSize: 12,
              color: theme.palette.text.primary,
            }}
          />
          <Bar dataKey="count" radius={[0, 3, 3, 0]}>
            {data.map((entry, idx) => (
              <Cell
                key={idx}
                fill={
                  idx === bestIdx
                    ? theme.palette.secondary.main
                    : theme.palette.info.main
                }
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {bestIdx >= 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.5, display: "block" }}
        >
          Выделенный столбец — лучший день для рекламы:{" "}
          <strong style={{ color: theme.palette.secondary.main }}>
            {WEEKDAY_NAMES[bestIdx]}
          </strong>{" "}
          ({data[bestIdx].count.toLocaleString("ru-RU")} сообщений)
        </Typography>
      )}
    </Box>
  )
}
